import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Stack,
  CircularProgress,
  Divider,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Collapse,
  Card,
  CardContent,
  Tabs,
  Tab
} from '@mui/material';
import { format, parseISO, addDays, isAfter, isBefore } from 'date-fns';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import EmailIcon from '@mui/icons-material/Email';
import DraftsIcon from '@mui/icons-material/Drafts';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CancelIcon from '@mui/icons-material/Cancel';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useDispatch } from 'react-redux';
import { generateClient } from 'aws-amplify/api';
import { listEMAILNOTIFICATIONS, listACTIVITIES } from 'graphql/queries';
import { listSCHEDULEDEMAILS, updateSCHEDULEDEMAILS } from 'graphql/scheduledEmails';
import EmailActivityFilters from './EmailActivityFilters';
import ReceivedFiles from './ReceivedFiles';
import { useLocation } from 'react-router-dom';

const API = generateClient();

const EmailPulse = ({ certificates, vendors, selectedClient, dateFilter, onDateFilterChange }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [sentEmails, setSentEmails] = useState([]);
  const [upcomingEmails, setUpcomingEmails] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sentPage, setSentPage] = useState(0);
  const [sentRowsPerPage, setSentRowsPerPage] = useState(10);
  const [upcomingPage, setUpcomingPage] = useState(0);
  const [upcomingRowsPerPage, setUpcomingRowsPerPage] = useState(10);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [expandedEmailTabs, setExpandedEmailTabs] = useState({});

  useEffect(() => {
    if (selectedClient?.id) {
      fetchEmailData();
    }
  }, [selectedClient, certificates]);

  // Check for document request ID in URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const requestId = params.get('request');
    if (requestId) {
      // Find the email with this document request ID
      const emailWithRequest = sentEmails.find(email => 
        email.documentRequestID === requestId || 
        email.metadata?.documentRequestID === requestId
      );
      
      if (emailWithRequest) {
        // Expand this email and switch to Received Files tab
        setExpandedRows(prev => new Set([...prev, emailWithRequest.id]));
        setExpandedEmailTabs(prev => ({
          ...prev,
          [emailWithRequest.id]: 1 // Switch to Received Files tab
        }));
      }
    }
  }, [location, sentEmails]);

  const fetchEmailData = async () => {
    setLoading(true);
    try {
      // Fetch email notifications
      const emailNotifications = await fetchEmailNotifications();
      
      // Also check certificates for emails sent today
      const todaysCertificateEmails = await checkCertificatesForTodaysEmails();
      
      // Merge both sources
      const allEmailData = [...emailNotifications, ...todaysCertificateEmails];
      
      // Fetch activities for additional context
      const activities = await fetchActivities();
      
      // Process sent emails
      const processedSentEmails = await processSentEmails(allEmailData, activities);
      setSentEmails(processedSentEmails);
      
      // Fetch scheduled emails from database
      const scheduledEmails = await fetchScheduledEmails();
      setUpcomingEmails(scheduledEmails);
      
    } catch (error) {
      console.error('Error fetching email data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmailNotifications = async () => {
    try {
      // First try to get all notifications without filter to debug
      const allResult = await API.graphql({
        query: listEMAILNOTIFICATIONS,
        variables: {
          limit: 1000
        }
      });
      
      const allNotifications = allResult.data.listEMAILNOTIFICATIONS.items || [];
      console.log('All email notifications:', allNotifications.length);
      
      // Log the most recent emails to debug
      const sortedByDate = [...allNotifications].sort((a, b) => 
        new Date(b.createdAt) - new Date(a.createdAt)
      );
      console.log('Most recent 5 emails:', sortedByDate.slice(0, 5).map(n => ({
        vendorName: n.vendorName,
        clientName: n.clientName,
        action: n.action,
        createdAt: n.createdAt,
        recipients: n.recipients
      })));
      
      // Filter by client name or check if vendor belongs to client
      const clientNotifications = allNotifications.filter(notification => {
        // Check if clientName matches
        if (notification.clientName === selectedClient.name) {
          return true;
        }
        
        // Check if vendor belongs to this client
        const vendorBelongsToClient = vendors.some(cv => 
          cv.vendor && (cv.vendor.name === notification.vendorName || cv.vendor.vendorName === notification.vendorName)
        );
        
        return vendorBelongsToClient;
      });
      
      console.log('Filtered email notifications for client:', clientNotifications.length);
      console.log('Looking for vendor "dfba standard":', clientNotifications.filter(n => n.vendorName?.toLowerCase().includes('dfba')));
      
      return clientNotifications;
    } catch (error) {
      console.error('Error fetching email notifications:', error);
      return [];
    }
  };

  const checkCertificatesForTodaysEmails = async () => {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      
      // Check certificates that have sentTo field populated today
      const certificatesWithEmails = certificates.filter(cert => {
        if (!cert.sentTo || cert.sentTo.length === 0) return false;
        
        // Check if certificate was updated today (when email was sent)
        const certUpdate = new Date(cert.updatedAt);
        return certUpdate >= today && certUpdate < tomorrow;
      });
      
      console.log('Certificates with emails sent today:', certificatesWithEmails.length);
      
      // Convert to email notification format
      const certificateEmails = certificatesWithEmails.map(cert => {
        const vendor = vendors.find(v => v.id === cert.clientVendorID)?.vendor;
        
        return {
          id: `cert-${cert.id}`,
          recipients: cert.sentTo,
          action: cert.status === 'REQUESTED' ? 'Request' : 'Custom',
          vendorName: vendor?.name || vendor?.vendorName || 'Unknown',
          clientName: selectedClient.name,
          createdAt: cert.updatedAt,
          sentBy: cert.sentBy,
          // Add mock data for missing fields
          uploadLink: '',
          subject: 'Certificate of Insurance Request',
          content: 'Certificate request sent via system'
        };
      });
      
      console.log('Certificate-based emails found:', certificateEmails);
      return certificateEmails;
    } catch (error) {
      console.error('Error checking certificates for emails:', error);
      return [];
    }
  };

  const fetchActivities = async () => {
    try {
      // Simplified query without complex filter
      const result = await API.graphql({
        query: listACTIVITIES,
        variables: {
          limit: 100
        }
      });
      
      // Filter in JavaScript instead
      const allActivities = result.data.listACTIVITIES.items || [];
      const emailActivities = allActivities.filter(activity => 
        activity.clientID === selectedClient.clientID && 
        activity.action && activity.action.includes("EMAIL")
      );
      
      return emailActivities;
    } catch (error) {
      console.error('Error fetching activities:', error);
      if (error.errors) {
        console.error('GraphQL errors:', error.errors);
      }
      return [];
    }
  };

  const fetchScheduledEmails = async () => {
    try {
      const result = await API.graphql({
        query: listSCHEDULEDEMAILS,
        variables: {
          filter: {
            and: [
              { clientID: { eq: selectedClient.clientID } },
              { status: { ne: "SENT" } } // Show all except sent emails
            ]
          },
          limit: 1000
        }
      });
      
      const scheduledEmails = result.data.listSCHEDULEDEMAILS.items || [];
      
      // Transform to match the expected format
      return scheduledEmails.map(email => ({
        id: email.id,
        subject: email.subject,
        scheduledDate: email.scheduledDate,
        recipients: email.recipients || [],
        vendorName: email.vendorName,
        vendorStatus: email.vendorStatus,
        certificateStatus: email.certificateStatus,
        type: 'Automated',
        action: email.action,
        certificateId: email.certificateID,
        vendorId: email.vendorID,
        expirationDate: email.expirationDate,
        status: email.status,
        content: email.content,
        greeting: email.greeting,
        signature: email.signature,
        uploadLink: email.uploadLink
      }));
    } catch (error) {
      console.error('Error fetching scheduled emails:', error);
      if (error.errors) {
        console.error('GraphQL errors:', error.errors);
      }
      return [];
    }
  };

  const processSentEmails = async (notifications, activities) => {
    const vendorLookup = new Map();
    vendors.forEach(cv => {
      if (cv.id && cv.vendor) {
        vendorLookup.set(cv.vendor.name || cv.vendor.vendorName, cv.vendor);
      }
    });

    console.log('Processing sent emails, total notifications:', notifications.length);
    
    // Log today's emails for debugging
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    const todaysEmails = notifications.filter(n => {
      const emailDate = new Date(n.createdAt);
      return emailDate >= today && emailDate < tomorrow;
    });
    console.log('Emails sent today:', todaysEmails.length, todaysEmails);
    
    // Remove duplicates - prefer EMAILNOTIFICATIONS over certificate-based entries
    const deduplicatedNotifications = notifications.filter(notification => {
      // Keep all non-certificate based emails
      if (!notification.id.startsWith('cert-')) return true;
      
      // For certificate-based, check if there's already an EMAILNOTIFICATIONS entry
      const hasDuplicate = notifications.some(n => 
        !n.id.startsWith('cert-') &&
        n.vendorName === notification.vendorName &&
        n.createdAt === notification.createdAt
      );
      
      return !hasDuplicate;
    });
    
    const sentEmails = deduplicatedNotifications.map(notification => {
      const vendor = vendorLookup.get(notification.vendorName);
      const isAutomated = ['Reminder1', 'Reminder7', 'Reminder14', 'CertificateExpiration'].includes(notification.action);
      
      // Find related activity for additional context
      const relatedActivity = activities.find(act => 
        act.createdAt === notification.createdAt && 
        act.vendorName === notification.vendorName
      );

      // Check if email was read by looking at certificate readBy field
      const relatedCert = certificates.find(cert => {
        const certVendor = vendors.find(v => v.id === cert.clientVendorID)?.vendor;
        return certVendor && (certVendor.name === notification.vendorName || certVendor.vendorName === notification.vendorName);
      });
      
      const isRead = relatedCert?.readBy?.some(email => 
        notification.recipients?.includes(email)
      ) || false;

      return {
        id: notification.id,
        subject: notification.subject || getDefaultSubject(notification.action),
        dateSent: notification.createdAt,
        recipients: notification.recipients || [],
        vendorName: notification.vendorName || 'Unknown',
        certificateStatus: getCertificateStatusAtTime(notification.vendorName, notification.createdAt),
        type: isAutomated ? 'Automated' : 'Manual',
        action: notification.action,
        vendorId: vendor?.id,
        isRead: isRead,
        content: notification.content || generateDefaultContent(notification.action, notification.vendorName, notification.clientName),
        greeting: notification.greeting,
        signature: notification.signature,
        uploadLink: notification.uploadLink,
        documentRequestID: notification.documentRequestID || notification.metadata?.documentRequestID,
        metadata: notification.metadata
      };
    });

    return sentEmails.sort((a, b) => new Date(b.dateSent) - new Date(a.dateSent));
  };


  const getDefaultSubject = (action) => {
    const subjects = {
      'Request': 'Certificate of Insurance Request',
      'Custom': 'Custom Email',
      'VendorUpload': 'Certificate Upload Confirmation',
      'ClientAdminNotificationOnUpload': 'New Certificate Uploaded',
      'Reminder1': 'Certificate Expires Tomorrow - Action Required',
      'Reminder7': 'Certificate Expires in 7 Days',
      'Reminder14': 'Certificate Expires in 14 Days',
      'CertificateExpiration': 'Certificate Has Expired',
      'ShareCertificate': 'Certificate Shared With You'
    };
    return subjects[action] || 'Certificate Notification';
  };

  const generateDefaultContent = (action, vendorName, clientName) => {
    const contents = {
      'Request': `We are requesting that you provide us with your current Certificate of Insurance. Please upload your certificate using the link provided.`,
      'Reminder1': `Your certificate of insurance expires tomorrow. Please upload your renewed certificate as soon as possible to maintain compliance.`,
      'Reminder7': `Your certificate of insurance will expire in 7 days. Please ensure you upload your renewed certificate before the expiration date.`,
      'Reminder14': `Your certificate of insurance will expire in 14 days. This is a friendly reminder to prepare your renewal documentation.`,
      'CertificateExpiration': `Your certificate of insurance has expired. Please upload your renewed certificate immediately to restore compliance.`,
      'VendorUpload': `Thank you for uploading your certificate. We have received it and will review it shortly.`,
      'Custom': `This is a custom message sent from the Certificate Management System.`
    };
    return contents[action] || 'Certificate notification from ' + (clientName || 'your client');
  };

  const getCertificateStatusAtTime = (vendorName, timestamp) => {
    // This is a simplified version - in production, you might want to 
    // track historical status from the activity log
    const vendor = vendors.find(v => 
      v.vendor?.name === vendorName || v.vendor?.vendorName === vendorName
    );
    
    if (!vendor) return 'Unknown';
    
    const vendorCerts = certificates.filter(c => c.clientVendorID === vendor.id);
    const relevantCert = vendorCerts.find(c => {
      const certDate = new Date(c.createdAt);
      const emailDate = new Date(timestamp);
      return certDate <= emailDate;
    });
    
    return relevantCert?.status || 'REQUESTED';
  };

  // Filter emails based on search term
  const filterEmails = (emails) => {
    if (!searchTerm) return emails;
    
    const term = searchTerm.toLowerCase();
    return emails.filter(email =>
      (email.vendorName || '').toLowerCase().includes(term) ||
      (email.recipients || []).some(r => (r || '').toLowerCase().includes(term)) ||
      (email.subject || '').toLowerCase().includes(term) ||
      (email.action || '').toLowerCase().includes(term)
    );
  };

  // Apply date filter
  const applyDateFilter = (emails, isUpcoming = false) => {
    if (!dateFilter || dateFilter === 'all') return emails;
    
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    let startDate;
    let endDate;

    switch (dateFilter) {
      case 'today':
        startDate = today;
        endDate = new Date(today);
        endDate.setDate(endDate.getDate() + 1);
        break;
      case '7days':
        startDate = new Date(today);
        startDate.setDate(startDate.getDate() - 7);
        endDate = new Date(today);
        endDate.setDate(endDate.getDate() + 1);
        break;
      case '30days':
        startDate = new Date(today);
        startDate.setDate(startDate.getDate() - 30);
        endDate = new Date(today);
        endDate.setDate(endDate.getDate() + 1);
        break;
      default:
        return emails;
    }

    return emails.filter(email => {
      const emailDate = new Date(isUpcoming ? email.scheduledDate : email.dateSent);
      if (isUpcoming) {
        // For upcoming emails, show those scheduled within the date range
        return emailDate >= startDate && emailDate < endDate;
      } else {
        // For sent emails, show those sent within the date range
        return emailDate >= startDate && emailDate < endDate;
      }
    });
  };

  const filteredSentEmails = useMemo(() => 
    applyDateFilter(filterEmails(sentEmails)), 
    [sentEmails, searchTerm, dateFilter]
  );

  const filteredUpcomingEmails = useMemo(() => 
    applyDateFilter(filterEmails(upcomingEmails), true), 
    [upcomingEmails, searchTerm, dateFilter]
  );

  const getStatusChip = (status, type = 'vendor') => {
    const statusColors = {
      vendor: {
        'ACTIVE': 'success',
        'ARCHIVED': 'default',
        'INACTIVE': 'warning'
      },
      certificate: {
        'APPROVED': 'success',
        'PENDING': 'warning',
        'REVIEW': 'info',
        'EXPIRED': 'error',
        'REQUESTED': 'default',
        'NOT_APPROVED': 'error'
      }
    };

    const color = statusColors[type]?.[status] || 'default';
    return <Chip label={status} color={color} size="small" />;
  };

  const getTypeChip = (type) => {
    return (
      <Chip 
        label={type} 
        color={type === 'Automated' ? 'primary' : 'secondary'} 
        size="small"
        variant="outlined"
      />
    );
  };

  const toggleRowExpanded = (rowId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(rowId)) {
      newExpanded.delete(rowId);
    } else {
      newExpanded.add(rowId);
      // Default to Sent Email tab when expanding
      if (!expandedEmailTabs[rowId]) {
        setExpandedEmailTabs(prev => ({
          ...prev,
          [rowId]: 0
        }));
      }
    }
    setExpandedRows(newExpanded);
  };

  const handlePauseEmail = async (emailId) => {
    try {
      const result = await API.graphql({
        query: updateSCHEDULEDEMAILS,
        variables: {
          input: {
            id: emailId,
            status: "PAUSED",
            pausedAt: new Date().toISOString(),
            pausedBy: "Current User" // You may want to get the actual user name
          }
        }
      });
      
      if (result.data.updateSCHEDULEDEMAILS) {
        // Refresh the data
        fetchEmailData();
      }
    } catch (error) {
      console.error('Error pausing email:', error);
    }
  };

  const handleCancelEmail = async (emailId) => {
    try {
      const result = await API.graphql({
        query: updateSCHEDULEDEMAILS,
        variables: {
          input: {
            id: emailId,
            status: "CANCELLED",
            cancelledAt: new Date().toISOString(),
            cancelledBy: "Current User" // You may want to get the actual user name
          }
        }
      });
      
      if (result.data.updateSCHEDULEDEMAILS) {
        // Refresh the data
        fetchEmailData();
      }
    } catch (error) {
      console.error('Error cancelling email:', error);
    }
  };

  const handleResumeEmail = async (emailId) => {
    try {
      const result = await API.graphql({
        query: updateSCHEDULEDEMAILS,
        variables: {
          input: {
            id: emailId,
            status: "PENDING",
            pausedAt: null,
            pausedBy: null
          }
        }
      });
      
      if (result.data.updateSCHEDULEDEMAILS) {
        // Refresh the data
        fetchEmailData();
      }
    } catch (error) {
      console.error('Error resuming email:', error);
    }
  };

  const handleMenuOpen = (event, email) => {
    setAnchorEl(event.currentTarget);
    setSelectedEmail(email);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedEmail(null);
  };

  const handleMenuAction = async (action) => {
    if (!selectedEmail) return;
    
    switch (action) {
      case 'pause':
        await handlePauseEmail(selectedEmail.id);
        break;
      case 'resume':
        await handleResumeEmail(selectedEmail.id);
        break;
      case 'cancel':
        await handleCancelEmail(selectedEmail.id);
        break;
    }
    
    handleMenuClose();
  };

  const renderEmailContent = (email) => {
    const currentTab = expandedEmailTabs[email.id] || 0;
    // Check if this email has document uploads - for Request emails or emails with documentRequestID
    const hasReceivedFiles = email.documentRequestID || email.metadata?.documentRequestID || 
                            email.action === 'Request' || email.action === 'Custom';
    
    return (
      <Box sx={{ m: 2 }}>
        {/* Tabs for expanded email */}
        {hasReceivedFiles ? (
          <Paper sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs 
              value={currentTab} 
              onChange={(e, newValue) => {
                setExpandedEmailTabs(prev => ({
                  ...prev,
                  [email.id]: newValue
                }));
              }}
            >
              <Tab label="Sent Email" />
              <Tab label="Received Files" />
            </Tabs>
          </Paper>
        ) : null}

        {/* Tab Panel 0: Sent Email (show always if no tabs, or when tab 0 selected) */}
        {(!hasReceivedFiles || currentTab === 0) && (
          <Card variant="outlined" sx={{ backgroundColor: '#f5f5f5' }}>
            <CardContent>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="subtitle2" color="textSecondary">From:</Typography>
                  <Typography variant="body2">{selectedClient.name || 'Certificate Management System'}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="textSecondary">To:</Typography>
                  <Typography variant="body2">{email.recipients.join(', ')}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="textSecondary">Date:</Typography>
                  <Typography variant="body2">{format(parseISO(email.dateSent), 'MMMM dd, yyyy h:mm a')}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="textSecondary">Subject:</Typography>
                  <Typography variant="body2">{email.subject}</Typography>
                </Box>
                <Divider />
                <Box>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {email.greeting || 'Hello,'}
                    {'\n\n'}
                    {email.content}
                    {'\n\n'}
                    {email.uploadLink && (
                      <>
                        {'Click here to upload your certificate: '}
                        <Typography component="span" color="primary" sx={{ textDecoration: 'underline' }}>
                          {email.uploadLink}
                        </Typography>
                        {'\n\n'}
                      </>
                    )}
                    {email.signature || 'Best regards,\nThe Certificate Management Team'}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        )}

        {/* Tab Panel 1: Received Files */}
        {currentTab === 1 && hasReceivedFiles && (
          <ReceivedFiles 
            documentRequestID={email.documentRequestID || email.metadata?.documentRequestID || email.id}
            selectedClient={selectedClient}
          />
        )}
      </Box>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={3}>
      {/* Search and Filters */}
      <Box display="flex" gap={2} alignItems="center">
            <TextField
              placeholder="Search by vendor, recipient, or subject..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              sx={{ flexGrow: 1, maxWidth: 400 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <EmailActivityFilters 
              dateFilter={dateFilter}
              onDateFilterChange={onDateFilterChange}
            />
          </Box>

          {/* Sent Emails Section */}
          <Paper>
        <Box padding={2}>
          <Typography variant="h6" gutterBottom>
            Sent Emails ({filteredSentEmails.length})
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width="40"></TableCell>
                  <TableCell>Date Sent</TableCell>
                  <TableCell>Subject</TableCell>
                  <TableCell>Recipients</TableCell>
                  <TableCell>Vendor</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Read Status</TableCell>
                  <TableCell>Certificate Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredSentEmails
                  .slice(sentPage * sentRowsPerPage, sentPage * sentRowsPerPage + sentRowsPerPage)
                  .map((email) => (
                    <React.Fragment key={email.id}>
                      <TableRow>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => toggleRowExpanded(email.id)}
                          >
                            {expandedRows.has(email.id) ? (
                              <ExpandLessIcon />
                            ) : (
                              <Stack direction="row" alignItems="center">
                                <ExpandMoreIcon />
                                <EmailIcon fontSize="small" sx={{ ml: -0.5 }} />
                              </Stack>
                            )}
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          {format(parseISO(email.dateSent), 'MMM dd, yyyy h:mm a')}
                        </TableCell>
                        <TableCell>{email.subject}</TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap">
                            {email.recipients.slice(0, 2).map((recipient, idx) => (
                              <Chip key={idx} label={recipient} size="small" variant="outlined" />
                            ))}
                            {email.recipients.length > 2 && (
                              <Chip label={`+${email.recipients.length - 2} more`} size="small" />
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>{email.vendorName}</TableCell>
                        <TableCell>{getTypeChip(email.type)}</TableCell>
                        <TableCell>
                          <Chip
                            icon={email.isRead ? <DraftsIcon /> : <MarkEmailReadIcon />}
                            label={email.isRead ? 'Read' : 'Unread'}
                            color={email.isRead ? 'success' : 'default'}
                            size="small"
                            variant={email.isRead ? 'filled' : 'outlined'}
                          />
                        </TableCell>
                        <TableCell>{getStatusChip(email.certificateStatus, 'certificate')}</TableCell>
                      </TableRow>
                      {expandedRows.has(email.id) && (
                        <TableRow>
                          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
                            <Collapse in={expandedRows.has(email.id)} timeout="auto" unmountOnExit>
                              {renderEmailContent(email)}
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={filteredSentEmails.length}
            rowsPerPage={sentRowsPerPage}
            page={sentPage}
            onPageChange={(e, newPage) => setSentPage(newPage)}
            onRowsPerPageChange={(e) => {
              setSentRowsPerPage(parseInt(e.target.value, 10));
              setSentPage(0);
            }}
          />
        </Box>
      </Paper>

      <Divider />

      {/* Upcoming Emails Section */}
      <Paper>
        <Box padding={2}>
          <Typography variant="h6" gutterBottom>
            Upcoming Emails ({filteredUpcomingEmails.length})
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Scheduled Date</TableCell>
                  <TableCell>Subject</TableCell>
                  <TableCell>Recipients</TableCell>
                  <TableCell>Vendor</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Certificate Expires</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUpcomingEmails
                  .slice(upcomingPage * upcomingRowsPerPage, upcomingPage * upcomingRowsPerPage + upcomingRowsPerPage)
                  .map((email) => (
                    <TableRow key={email.id}>
                      <TableCell>
                        {format(parseISO(email.scheduledDate), 'MMM dd, yyyy')}
                      </TableCell>
                      <TableCell>{email.subject}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap">
                          {email.recipients.slice(0, 2).map((recipient, idx) => (
                            <Chip key={idx} label={recipient} size="small" variant="outlined" />
                          ))}
                          {email.recipients.length > 2 && (
                            <Chip label={`+${email.recipients.length - 2} more`} size="small" />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>{email.vendorName}</TableCell>
                      <TableCell>{getTypeChip(email.type)}</TableCell>
                      <TableCell>
                        <Chip
                          label={email.status}
                          color={
                            email.status === 'PENDING' ? 'primary' :
                            email.status === 'PAUSED' ? 'warning' :
                            email.status === 'CANCELLED' ? 'error' : 'default'
                          }
                          size="small"
                          icon={
                            email.status === 'PAUSED' ? <PauseIcon /> :
                            email.status === 'CANCELLED' ? <CancelIcon /> : null
                          }
                        />
                      </TableCell>
                      <TableCell>
                        {format(parseISO(email.expirationDate), 'MMM dd, yyyy')}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, email)}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={filteredUpcomingEmails.length}
            rowsPerPage={upcomingRowsPerPage}
            page={upcomingPage}
            onPageChange={(e, newPage) => setUpcomingPage(newPage)}
            onRowsPerPageChange={(e) => {
              setUpcomingRowsPerPage(parseInt(e.target.value, 10));
              setUpcomingPage(0);
            }}
          />
        </Box>
      </Paper>

          {/* Action Menu */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            {selectedEmail?.status === 'PENDING' && (
              <MenuItem onClick={() => handleMenuAction('pause')}>
                <PauseIcon sx={{ mr: 1 }} fontSize="small" />
                Pause Email
              </MenuItem>
            )}
            {selectedEmail?.status === 'PAUSED' && (
              <MenuItem onClick={() => handleMenuAction('resume')}>
                <PlayArrowIcon sx={{ mr: 1 }} fontSize="small" />
                Resume Email
              </MenuItem>
            )}
            {selectedEmail?.status !== 'CANCELLED' && (
              <MenuItem onClick={() => handleMenuAction('cancel')} sx={{ color: 'error.main' }}>
                <CancelIcon sx={{ mr: 1 }} fontSize="small" />
                Cancel Email
              </MenuItem>
            )}
          </Menu>
    </Stack>
  );
};

export default EmailPulse;